import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { localDayKey } from "@/lib/format";
import { toast } from "sonner";
import { Check, CalendarClock, Calendar, Loader2, X } from "lucide-react";
import { useModalBack } from "@/lib/modalBack";

// "Catch up" sheet — every past-due schedule item with one-thumb actions:
// Done / Move to today / Reschedule, plus Mark all done. Undo toast after
// each change; nothing happens automatically.
// Owner/admin can act on any item; crew only on items assigned to them.
export default function CatchUpSheet({ items, project, user, onClose, onChanged, onEdit, testidPrefix = "catchup" }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [doneIds, setDoneIds] = useState(new Set());
  const [movedIds, setMovedIds] = useState(new Set());
  useModalBack(true, onClose);

  const today = localDayKey();
  const canMark = (s) => ["owner", "admin"].includes(user?.role) || (s.crew_ids || []).includes(user?.id);
  const overdue = (items || [])
    .filter((s) => !s.deleted_at && s.status !== "done" && !s.done_at && String(s.end || s.date || "").slice(0, 10) < today)
    .sort((a, b) => String(a.end || a.date || "").localeCompare(String(b.end || b.date || "")));
  const open = overdue.filter((s) => !doneIds.has(s.id) && !movedIds.has(s.id));
  const actionable = open.filter(canMark);
  const daysLate = (s) => Math.max(1, Math.round((Date.now() - new Date(`${String(s.end || s.date || "").slice(0, 10)}T12:00:00`).getTime()) / 86400000));

  const undoToast = (msg, undoFn) => toast.success(msg, {
    action: { label: t("sched_undo"), onClick: async () => { try { await undoFn(); } catch { toast.error(t("toast_failed")); } } },
    duration: 8000,
  });

  const markDone = async (s) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/schedule/${s.id}`, { status: "done" });
      setDoneIds((d) => new Set([...d, s.id]));
      onChanged?.();
      undoToast(t("toast_saved"), async () => {
        await api.patch(`/schedule/${s.id}`, { status: "in_progress" });
        setDoneIds((d) => { const n = new Set(d); n.delete(s.id); return n; });
        onChanged?.();
      });
    } catch (e) { toast.error(e?.response?.status === 403 ? t("sheet_owner_only") : t("toast_failed")); }
    setBusy(false);
  };

  const moveToday = async (s) => {
    if (busy) return;
    setBusy(true);
    const prev = { date: s.date, end: s.end || null };
    try {
      const hasTime = (s.date || "").length > 10;
      const payload = { date: hasTime ? `${today}T${s.date.slice(11, 16)}` : today };
      if (s.end) payload.end = s.end.length > 10 ? `${today}T${s.end.slice(11, 16)}` : today;
      await api.patch(`/schedule/${s.id}`, payload);
      setMovedIds((d) => new Set([...d, s.id]));
      onChanged?.();
      undoToast(t("toast_moved"), async () => {
        await api.patch(`/schedule/${s.id}`, { date: prev.date, end: prev.end });
        setMovedIds((d) => { const n = new Set(d); n.delete(s.id); return n; });
        onChanged?.();
      });
    } catch (e) { toast.error(e?.response?.status === 403 ? t("sheet_owner_only") : t("toast_failed")); }
    setBusy(false);
  };

  const markAll = async () => {
    if (busy || !actionable.length) return;
    setBusy(true);
    const ok = [];
    for (const s of actionable) {
      try { await api.patch(`/schedule/${s.id}`, { status: "done" }); ok.push(s.id); } catch { /* keep going */ }
    }
    setDoneIds((d) => new Set([...d, ...ok]));
    onChanged?.();
    undoToast(t("toast_saved"), async () => {
      for (const sid of ok) { try { await api.patch(`/schedule/${sid}`, { status: "in_progress" }); } catch { /* keep going */ } }
      setDoneIds((d) => { const n = new Set(d); ok.forEach((sid) => n.delete(sid)); return n; });
      onChanged?.();
    });
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" role="dialog" aria-modal="true"
      data-testid={`${testidPrefix}-sheet`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-t-2xl bg-white p-5 pb-8 sm:rounded-2xl sm:pb-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate font-heading text-lg font-extrabold text-slate-900" data-testid={`${testidPrefix}-title`}>
              {t("catchup_title")}{project?.name ? ` · ${project.name}` : ""}
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{t("catchup_hint")}</p>
          </div>
          <button onClick={onClose} data-testid={`${testidPrefix}-close`} aria-label={t("close")}
            className="min-h-[44px] min-w-[44px] rounded-full p-2 text-slate-500 hover:text-slate-900 flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        {open.length === 0 ? (
          <p className="mt-6 text-center text-sm text-slate-500" data-testid={`${testidPrefix}-empty`}>{t("catchup_empty")}</p>
        ) : (
          <div className="mt-4 space-y-2" data-testid={`${testidPrefix}-list`}>
            {open.map((s) => (
              <div key={s.id} className="rounded-xl border border-red-200 bg-red-50/50 px-3 py-2.5" data-testid={`${testidPrefix}-row-${s.id}`}>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-slate-900">{s.title || t("nav_schedule")}</div>
                    <div className="text-[11px] font-semibold text-red-600">
                      {!project && s.project_name ? `${s.project_name} · ` : ""}{t("sched_overdue_chip", { n: daysLate(s) })}
                    </div>
                  </div>
                  {canMark(s) && (
                    <button onClick={() => markDone(s)} disabled={busy} data-testid={`${testidPrefix}-done-${s.id}`}
                      className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-green-600 px-4 text-xs font-bold text-white active:scale-95 disabled:opacity-50">
                      <Check className="h-4 w-4" />{t("catchup_done")}
                    </button>
                  )}
                </div>
                {canMark(s) && (
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => moveToday(s)} disabled={busy} data-testid={`${testidPrefix}-today-${s.id}`}
                      className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white text-xs font-bold text-slate-700 active:scale-95 disabled:opacity-50">
                      <CalendarClock className="h-3.5 w-3.5" />{t("catchup_today")}
                    </button>
                    {onEdit && (
                      <button onClick={() => onEdit(s)} disabled={busy} data-testid={`${testidPrefix}-edit-${s.id}`}
                        className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white text-xs font-bold text-slate-700 active:scale-95 disabled:opacity-50">
                        <Calendar className="h-3.5 w-3.5" />{t("catchup_reschedule")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {actionable.length > 1 && (
          <button onClick={markAll} disabled={busy} data-testid={`${testidPrefix}-mark-all`}
            className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-green-600 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("catchup_mark_all")}
          </button>
        )}
      </div>
    </div>
  );
}
